import React, { useEffect, useState, useRef } from 'react';
import { api, apiError } from '../lib/api';
import { MapPicker } from './MapPicker';

/* ============================================================
   Reusable image input: URL + Upload (used for Logo, Cover, Gallery)
   ============================================================ */
function ImageField({
  label,
  value,
  onChange,
  aspect = 'square',                 // 'square' | 'wide'
  previewSize = { width: 64, height: 64 },
  testIdPrefix = 'img',
  helpText = 'JPG, PNG, GIF or WEBP · max 5 MB',
  required = false,
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const uploadFile = async (file) => {
    if (!file) return;
    setErr('');
    if (file.size > 5 * 1024 * 1024) {
      setErr('Image is too large. Max size is 5 MB.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url =
        data?.url || data?.file_url || data?.image_url ||
        data?.path || data?.data?.url || '';
      if (!url) throw new Error('Upload succeeded but no URL was returned.');
      onChange(url);
    } catch (e) {
      setErr(
        'Upload failed: ' +
        (apiError(e) || e.message || 'Unknown error') +
        '. You can still paste an image URL.'
      );
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  return (
    <div>
      <label className="form-label small fw-semibold mb-1">
        {label}{required ? ' *' : ''}
      </label>
      {err && <div className="alert alert-danger py-1 px-2 small mb-2">{err}</div>}

      <div className="d-flex gap-2 align-items-start">
        {/* Preview */}
        <div
          style={{
            width: previewSize.width,
            height: previewSize.height,
            flex: `0 0 ${previewSize.width}px`,
            borderRadius: aspect === 'wide' ? 10 : 12,
            border: '1px dashed #cbd5e1',
            background: '#F8FAFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {value ? (
            <img
              src={value}
              alt={`${label} preview`}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                e.currentTarget.style.opacity = '0.3';
                e.currentTarget.title = 'Preview unavailable';
              }}
            />
          ) : (
            <span className="text-muted small" style={{ fontSize: '0.7rem' }}>
              {aspect === 'wide' ? 'Cover' : 'Logo'}
            </span>
          )}
          {uploading && (
            <div
              style={{
                position: 'absolute', inset: 0,
                background: 'rgba(255,255,255,0.75)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.65rem', fontWeight: 600, color: '#4338CA',
              }}
            >
              …
            </div>
          )}
        </div>

        {/* URL input + buttons */}
        <div className="flex-grow-1">
          <div style={{ position: 'relative' }}>
            <input
              className="form-control"
              placeholder="Paste URL, or click Upload →"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              style={{ paddingRight: value ? 38 : 12 }}
              data-testid={`${testIdPrefix}-url`}
            />
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                style={{
                  position: 'absolute', right: 6, top: '50%',
                  transform: 'translateY(-50%)',
                  border: 'none', background: 'transparent',
                  cursor: 'pointer', color: '#94a3b8',
                  fontSize: '1.1rem', lineHeight: 1,
                }}
                aria-label="Clear"
              >
                ×
              </button>
            )}
          </div>
          <div className="d-flex gap-2 mt-1">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid={`${testIdPrefix}-upload-btn`}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            {value && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => onChange('')}
                disabled={uploading}
              >
                Remove
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onPickFile}
            data-testid={`${testIdPrefix}-file`}
          />
          {helpText && (
            <div className="form-text small text-muted-2 mt-1">{helpText}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Inline gallery manager (only shown when editing an existing business)
   ============================================================ */
function InlineGallery({ businessId, initialImages = [], onChange }) {
  const [images, setImages] = useState(initialImages);
  const [imageUrl, setImageUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  useEffect(() => { setImages(initialImages); }, [initialImages]);

  const sync = (next) => {
    setImages(next);
    if (onChange) onChange(next);
  };

  const add = async (e) => {
    e.preventDefault();
    setErr('');
    if (!imageUrl.trim()) {
      setErr('Please paste an image URL or upload a file first.');
      return;
    }
    if (!businessId) {
      setErr('Save the business once before adding gallery images.');
      return;
    }
    try {
      const { data } = await api.post(`/owner/businesses/${businessId}/gallery`, {
        image_url: imageUrl,
        caption,
      });
      const newImage = data?.gallery || data?.image || {
        id: Date.now(),
        image_url: imageUrl,
        caption,
      };
      sync([...(images || []), newImage]);
      setImageUrl('');
      setCaption('');
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      setErr(apiError(e) || 'Failed to add image.');
    }
  };

  const del = async (g) => {
    if (!businessId) return;
    if (!window.confirm('Remove image?')) return;
    try {
      await api.delete(`/owner/businesses/${businessId}/gallery/${g.id}`);
      sync((images || []).filter((x) => x.id !== g.id));
    } catch (e) {
      setErr(apiError(e) || 'Failed to delete image.');
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setErr('');
    if (file.size > 5 * 1024 * 1024) {
      setErr('Image is too large. Max size is 5 MB.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url =
        data?.url || data?.file_url || data?.image_url ||
        data?.path || data?.data?.url || '';
      if (!url) throw new Error('Upload succeeded but no URL was returned.');
      setImageUrl(url);
    } catch (e) {
      setErr(
        'Upload failed: ' +
        (apiError(e) || e.message || 'Unknown error') +
        '. You can still paste an image URL manually.'
      );
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  return (
    <div
      className="p-3 rounded"
      style={{ background: '#F8FAFF', border: '1px solid #E0E7FF' }}
      data-testid="inline-gallery"
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="fw-semibold small">Gallery</div>
        <span className="small text-muted-2">
          {(images || []).length} image{(images || []).length === 1 ? '' : 's'}
        </span>
      </div>

      {err && <div className="alert alert-danger py-1 px-2 small mb-2">{err}</div>}

      {!businessId && (
        <div className="alert alert-info py-1 px-2 small mb-2">
          Save the business first, then you can add gallery images here or in the Gallery tab.
        </div>
      )}

      <form onSubmit={add} className="row g-2 mb-3 align-items-end">
        <div className="col-md-6">
          <label className="form-label small fw-semibold mb-1">Image</label>
          <div className="d-flex gap-2">
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                className="form-control form-control-sm"
                placeholder="Paste URL or upload →"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                style={{ paddingRight: imageUrl ? 34 : 10 }}
                data-testid="inline-gal-url"
              />
              {imageUrl && (
                <button
                  type="button"
                  onClick={() => { setImageUrl(''); if (fileRef.current) fileRef.current.value = ''; }}
                  style={{
                    position: 'absolute', right: 6, top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none', background: 'transparent',
                    cursor: 'pointer', color: '#94a3b8',
                    fontSize: '1rem', lineHeight: 1,
                  }}
                  aria-label="Clear"
                >
                  ×
                </button>
              )}
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || !businessId}
              data-testid="inline-gal-upload-btn"
            >
              {uploading ? '…' : 'Upload'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onPickFile}
            />
          </div>
        </div>
        <div className="col-md-3">
          <label className="form-label small fw-semibold mb-1">Caption</label>
          <input
            className="form-control form-control-sm"
            placeholder="Optional"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            data-testid="inline-gal-cap"
          />
        </div>
        <div className="col-md-2">
          <button
            className="btn-ap btn-sm w-100"
            disabled={!businessId || uploading || !imageUrl.trim()}
            data-testid="inline-gal-add"
          >
            Add
          </button>
        </div>
      </form>

      {imageUrl && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <img
            src={imageUrl}
            alt="Preview"
            style={{
              width: 64, height: 48, objectFit: 'cover',
              borderRadius: 6, border: '1px solid #e2e8f0',
            }}
            onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
          />
          <span className="small text-muted-2">Preview</span>
        </div>
      )}

      <div className="row g-2">
        {(images || []).map((g) => (
          <div className="col-4 col-md-3" key={g.id}>
            <div style={{ position: 'relative' }}>
              <img
                src={g.image_url}
                alt={g.caption || ''}
                style={{
                  width: '100%', height: 80, objectFit: 'cover',
                  borderRadius: 8,
                }}
              />
              <button
                type="button"
                className="btn btn-sm btn-danger"
                style={{
                  position: 'absolute', top: 4, right: 4,
                  padding: '0.1rem 0.45rem', fontSize: '0.7rem',
                }}
                onClick={() => del(g)}
                title="Remove"
              >
                ×
              </button>
            </div>
          </div>
        ))}
        {(images || []).length === 0 && (
          <div className="col-12 text-center text-muted small py-2">
            No images yet.
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Main BusinessForm
   ============================================================ */
export default function BusinessForm({ initial, onSubmit, submitLabel = 'Save' }) {
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [subs, setSubs] = useState([]);
  const [f, setF] = useState({
    name:'', tagline:'', description:'', address:'', phone:'', whatsapp:'', email:'', website:'',
    logo_url:'', cover_url:'', facebook_url:'', instagram_url:'', twitter_url:'', linkedin_url:'', youtube_url:'',
    category_id:'', subcategory_id:'', city_id:'', latitude:'', longitude:'', ...(initial||{})
  });
  const [gallery, setGallery] = useState(initial?.gallery || []);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, ci] = await Promise.all([api.get('/categories'), api.get('/cities')]);
      setCats(c.data.categories||[]);
      setCities(ci.data.cities||[]);
    })();
  }, []);

  useEffect(() => {
    if (f.category_id) api.get('/subcategories?category_id=' + f.category_id).then(r => setSubs(r.data.subcategories||[]));
    else setSubs([]);
  }, [f.category_id]);

  // Keep gallery in sync if `initial` changes (e.g. after reload)
  useEffect(() => {
    setGallery(initial?.gallery || []);
  }, [initial?.gallery]);

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const payload = { ...f };
      payload.category_id = Number(payload.category_id);
      payload.city_id = Number(payload.city_id);
      if (payload.subcategory_id) payload.subcategory_id = Number(payload.subcategory_id);
      else delete payload.subcategory_id;
      if (!payload.latitude) delete payload.latitude;
      if (!payload.longitude) delete payload.longitude;
      await onSubmit(payload);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  const field = (label, key, props={}) => (
    <div className="mb-2">
      <label className="form-label">{label}</label>
      <input className="form-control" value={f[key]||''} onChange={e=>setF({...f,[key]:e.target.value})} data-testid={`bf-${key}`} {...props}/>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="business-form">
      {err && <div className="alert alert-danger">{err}</div>}
      <div className="row g-2">
        <div className="col-md-6">{field('Business name', 'name', { required:true })}</div>
        <div className="col-md-6">{field('Tagline', 'tagline')}</div>
        <div className="col-md-4">
          <label className="form-label">Category</label>
          <select className="form-select" required value={f.category_id||''} onChange={e=>setF({...f, category_id:e.target.value, subcategory_id:''})} data-testid="bf-category_id">
            <option value="">Select</option>
            {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label">Subcategory</label>
          <select className="form-select" value={f.subcategory_id||''} onChange={e=>setF({...f, subcategory_id:e.target.value})} data-testid="bf-subcategory_id">
            <option value="">—</option>
            {subs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label">City</label>
          <select className="form-select" required value={f.city_id||''} onChange={e=>setF({...f, city_id:e.target.value})} data-testid="bf-city_id">
            <option value="">Select</option>
            {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="col-12">
          <label className="form-label">Description</label>
          <textarea className="form-control" rows={4} value={f.description||''} onChange={e=>setF({...f, description:e.target.value})} data-testid="bf-description" />
        </div>
        <div className="col-md-8">{field('Address', 'address')}</div>
        <div className="col-md-2">{field('Latitude', 'latitude')}</div>
        <div className="col-md-2">{field('Longitude', 'longitude')}</div>
        <div className="col-12">
          <label className="form-label d-flex justify-content-between align-items-center">
            <span>Pin location on map</span>
            {navigator.geolocation && (
              <button type="button" className="btn-ap-outline btn-sm" onClick={() => {
                navigator.geolocation.getCurrentPosition(
                  (pos) => setF(prev => ({ ...prev, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) })),
                  (err) => alert('Could not get your location: ' + err.message),
                  { enableHighAccuracy: true, timeout: 8000 }
                );
              }} data-testid="use-my-location">📍 Use my location</button>
            )}
          </label>
          <MapPicker
            latitude={f.latitude}
            longitude={f.longitude}
            onChange={({ latitude, longitude }) => setF(prev => ({ ...prev, latitude, longitude }))}
          />
          <div className="small text-muted-2 mt-1">Click or drag the marker to set the exact location.</div>
        </div>
        <div className="col-md-4">{field('Phone', 'phone')}</div>
        <div className="col-md-4">{field('WhatsApp', 'whatsapp')}</div>
        <div className="col-md-4">{field('Email', 'email', { type:'email' })}</div>
        <div className="col-md-6">{field('Website', 'website')}</div>

        {/* --- Logo --- */}
        <div className="col-md-6">
          <ImageField
            label="Logo"
            value={f.logo_url}
            onChange={(url) => setF((prev) => ({ ...prev, logo_url: url }))}
            aspect="square"
            previewSize={{ width: 64, height: 64 }}
            testIdPrefix="bf-logo"
          />
        </div>

        {/* --- Cover --- */}
        <div className="col-md-6">
          <ImageField
            label="Cover image"
            value={f.cover_url}
            onChange={(url) => setF((prev) => ({ ...prev, cover_url: url }))}
            aspect="wide"
            previewSize={{ width: 96, height: 64 }}
            testIdPrefix="bf-cover"
          />
        </div>

        <div className="col-md-6">{field('Facebook', 'facebook_url')}</div>
        <div className="col-md-6">{field('Instagram', 'instagram_url')}</div>
        <div className="col-md-6">{field('Twitter/X', 'twitter_url')}</div>
        <div className="col-md-6">{field('LinkedIn', 'linkedin_url')}</div>
        <div className="col-md-6">{field('YouTube', 'youtube_url')}</div>

        {/* --- Inline gallery --- */}
        <div className="col-12 mt-3">
          <InlineGallery
            businessId={initial?.id}
            initialImages={gallery}
            onChange={setGallery}
          />
        </div>
      </div>
      <button className="btn-ap mt-3" disabled={busy} data-testid="bf-submit">{busy ? 'Saving…' : submitLabel}</button>
    </form>
  );
}